import React, { useEffect, useState } from "react";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { formatCurrencyUY } from "../../utils/formatters";
import { tolerantId, fetchUsersByUserIds } from "../../utils/firestoreHelpers";
import {
  FaExclamationCircle,
  FaPlus,
  FaUser,
  FaCheck,
  FaBan,
  FaSearch,
  FaReceipt,
  FaMoneyBillWave,
  FaPen,
} from "react-icons/fa";
import AdminModal from "../../components/AdminModal";
import { colors, shadows, radius, spacing, typography, transitions, cardBase, btnPrimary, btnSecondary, btnDanger, inputBase, pageTitle } from "../../utils/designTokens";
import { getAllActiveFunds } from "../../utils/fundHelpers";
import StatusBadge from "../../components/StatusBadge";
import LoadingState from "../../components/LoadingState";
import { useLatestRate } from "../../utils/exchangeRates";

// Conceptos frecuentes de cargo puntual (sugerencias)
const CONCEPT_SUGGESTIONS = [
  "Control remoto de TV",
  "Reposición de llave / tarjeta de acceso",
  "Rotura de espacio común",
  "Uso excepcional de servicio",
  "Otro",
];

const CargosPuntualesAdmin = () => {
  const { showToast } = useToast();
  const { selectedBuilding } = useBuilding();
  const { rate: bcuRate, loading: bcuLoading } = useLatestRate();
  const buildingId = selectedBuilding?.buildingid;

  const [cargos, setCargos] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("todos");
  const [search, setSearch] = useState("");

  // Modales
  const [annulModal, setAnnulModal] = useState(null);
  const [annulReason, setAnnulReason] = useState("");
  const [payModal, setPayModal] = useState(null);
  const [editModal, setEditModal] = useState(null); // cargo en edición (solo status "activa")

  const emptyForm = {
    unitId: "",
    userid: "",
    userType: "owner",
    amount: "",
    currency: "UYU",
    concept: "",
    targetFundId: "caja",
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!buildingId) return;
    fetchCargos();
    fetchApartments();
  }, [buildingId]);

  const fetchCargos = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "apartmentcharges"),
        where("buildingid", "in", tolerantId(buildingId))
      );
      const snap = await getDocs(q);
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setCargos(list);

      // Nombres: fichas del edificio (reglas 0D, C10: accessBids
      // array-contains). Best-effort: sin ellas la lista dice "ID n".
      const userIds = [...new Set(list.map((c) => c.userid).filter(Boolean))];
      if (userIds.length > 0) {
        try {
          const users = await fetchUsersByUserIds(userIds, buildingId);
          const map = {};
          users.forEach((u) => {
            map[String(u.userid)] = u.NombreCompleto || u.email || `ID ${u.userid}`;
          });
          setUsersMap((prev) => ({ ...prev, ...map }));
        } catch (usersErr) {
          console.warn("Cargos puntuales: no se pudieron leer las fichas de los residentes:", usersErr?.code || usersErr?.message);
        }
      }
    } catch (err) {
      console.error("Error fetching cargos:", err);
      showToast("Error al cargar los cargos", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchApartments = async () => {
    try {
      const q = query(
        collection(db, "apartments"),
        where("building", "in", tolerantId(buildingId))
      );
      const snap = await getDocs(q);
      const apts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setApartments(apts);

      const aptUserIds = [];
      apts.forEach((apt) => {
        [apt.owner, apt.ownerSecondary, apt.tenant, apt.tenantSecondary]
          .filter(Boolean)
          .forEach((id) => aptUserIds.push(id));
      });
      if (aptUserIds.length > 0) {
        const users = await fetchUsersByUserIds(aptUserIds, buildingId);
        setUsersMap((prev) => {
          const updated = { ...prev };
          users.forEach((u) => {
            updated[String(u.userid)] = u.NombreCompleto || u.email || `ID ${u.userid}`;
          });
          return updated;
        });
      }
    } catch (err) {
      console.error("Error fetching apartments:", err);
    }
  };

  const getUnitUsers = () => {
    const apt = apartments.find((a) => a.id === form.unitId);
    if (!apt) return { owner: null, tenant: null };
    return {
      owner: apt.owner ? { id: apt.owner, label: `Propietario (${apt.owner})` } : null,
      tenant: apt.tenant ? { id: apt.tenant, label: `Inquilino (${apt.tenant})` } : null,
    };
  };

  useEffect(() => {
    if (!form.unitId) {
      setForm((f) => ({ ...f, userid: "", userType: "owner" }));
      return;
    }
    const users = getUnitUsers();
    if (form.userType === "owner" && users.owner) {
      setForm((f) => ({ ...f, userid: users.owner.id }));
    } else if (form.userType === "tenant" && users.tenant) {
      setForm((f) => ({ ...f, userid: users.tenant.id }));
    } else if (users.owner) {
      setForm((f) => ({ ...f, userid: users.owner.id, userType: "owner" }));
    } else {
      setForm((f) => ({ ...f, userid: "", userType: "owner" }));
    }
  }, [form.unitId, form.userType, apartments]);

  const handleCreate = async () => {
    if (!form.unitId) {
      showToast("Elegí una unidad", "error");
      return;
    }
    if (!form.userid) {
      showToast(
        `La Unidad ${apartments.find((a) => a.id === form.unitId)?.number || ""} no tiene ${form.userType === "tenant" ? "un inquilino" : "un propietario"} asignado. Elegí otra unidad, cambiá el responsable, o asigná primero a la persona.`,
        "error"
      );
      return;
    }
    if (!form.concept.trim()) {
      showToast("Ingresá el concepto del cargo", "error");
      return;
    }
    const amount = Number(form.amount);
    if (!form.amount || amount <= 0) {
      showToast("Ingresá un monto mayor a 0", "error");
      return;
    }

    setSaving(true);
    try {
      const apt = apartments.find((a) => a.id === form.unitId);
      await addDoc(collection(db, "apartmentcharges"), {
        buildingid: Number(buildingId),
        unitId: form.unitId,
        unitNumber: apt?.number || "",
        userid: form.userid,
        userType: form.userType,
        amount,
        currency: form.currency || "UYU",
        concept: form.concept.trim(),
        chargeType: "cargo_puntual",
        status: "activa",
        targetFundId: form.targetFundId || "caja",
        createdAt: Timestamp.now(),
        createdBy: auth.currentUser?.email || "",
        paidAt: null,
        annulledAt: null,
        annulledBy: null,
        annulledReason: null,
        includedInCierreId: null,
      });

      showToast("Cargo creado correctamente", "success");
      setShowForm(false);
      setForm(emptyForm);
      fetchCargos();
    } catch (err) {
      console.error("Error creating cargo:", err);
      showToast("Error al crear el cargo", "error");
    } finally {
      setSaving(false);
    }
  };

  // Editar monto/concepto/fondo — solo mientras está "activa" (aún no entró al cierre)
  const handleSaveEdit = async () => {
    if (!editModal) return;
    const amount = Number(editModal.amount);
    if (!editModal.concept?.trim() || amount <= 0) {
      showToast("Completá concepto y monto (mayor a 0)", "error");
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, "apartmentcharges", editModal.id), {
        amount,
        concept: editModal.concept.trim(),
        currency: editModal.currency || "UYU",
        targetFundId: editModal.targetFundId || "caja",
        updatedAt: Timestamp.now(),
        updatedBy: auth.currentUser?.email || "",
      });
      showToast("Cargo actualizado", "success");
      setEditModal(null);
      fetchCargos();
    } catch (err) {
      console.error("Error editing cargo:", err);
      showToast("Error al actualizar el cargo", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async (cargo) => {
    try {
      await updateDoc(doc(db, "apartmentcharges", cargo.id), {
        status: "pagada",
        paidAt: Timestamp.now(),
      });
      showToast("Cargo marcado como pagado", "success");
      setPayModal(null);
      fetchCargos();
    } catch (err) {
      console.error("Error marking cargo paid:", err);
      showToast("Error al actualizar el cargo", "error");
    }
  };

  const handleAnnul = async () => {
    if (!annulReason.trim()) {
      showToast("Ingresá el motivo de anulación", "error");
      return;
    }
    try {
      await updateDoc(doc(db, "apartmentcharges", annulModal.id), {
        status: "anulada",
        annulledAt: Timestamp.now(),
        annulledBy: auth.currentUser?.email || "",
        annulledReason: annulReason.trim(),
      });
      showToast("Cargo anulado", "success");
      setAnnulModal(null);
      setAnnulReason("");
      fetchCargos();
    } catch (err) {
      console.error("Error annulling cargo:", err);
      showToast("Error al anular el cargo", "error");
    }
  };

  const filtered = cargos.filter((c) => {
    if (filter !== "todos" && c.status !== filter) return false;
    if (search.trim()) {
      const s = search.toLowerCase();
      const name = (usersMap[String(c.userid)] || "").toLowerCase();
      return (
        (c.concept || "").toLowerCase().includes(s) ||
        (c.unitNumber || "").toString().toLowerCase().includes(s) ||
        name.includes(s)
      );
    }
    return true;
  });

  const stats = {
    total: cargos.length,
    activos: cargos.filter((c) => c.status === "activa").length,
    incluidos: cargos.filter((c) => c.status === "incluida_en_cierre").length,
    montoActivo: cargos
      .filter((c) => c.status === "activa")
      .reduce((sum, c) => sum + (Number(c.amount) || 0), 0),
  };

  const formatDate = (val) => {
    if (!val) return "-";
    const d = val.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const statusLabel = {
    activa: "Activo",
    incluida_en_cierre: "En cierre",
    pagada: "Pagado",
    anulada: "Anulado",
  };
  const statusVariant = {
    activa: "warning",
    incluida_en_cierre: "info",
    pagada: "success",
    anulada: "neutral",
  };

  if (!buildingId) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <FaExclamationCircle size={48} color={colors.gray400} />
          <p style={{ color: colors.gray600, marginTop: 16 }}>Seleccioná un edificio para ver los cargos</p>
        </div>
      </div>
    );
  }

  const fundName = (fundId) =>
    getAllActiveFunds(selectedBuilding).find((f) => f.id === (fundId || "caja"))?.name || "Caja";

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>
            <FaReceipt style={{ marginRight: 8 }} /> Cargos puntuales
          </h2>
          <p style={styles.subtitle}>
            Gastos puntuales que se cobran a una unidad (se suman al próximo cierre de mes)
          </p>
        </div>
        <button style={styles.btnPrimary} onClick={() => { setForm(emptyForm); setShowForm(true); }}>
          <FaPlus size={14} /> Nuevo cargo
        </button>
      </div>

      {!bcuLoading && bcuRate && (bcuRate.ui || bcuRate.ur) && (
        <div style={{ ...cardBase, padding: spacing[4], marginBottom: spacing[4] }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: spacing[3] }}>
            <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Valores BCU ({bcuRate.date})</div>
            <div style={{ display: "flex", gap: spacing[6], flexWrap: "wrap" }}>
              {bcuRate.ui && <span style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>UI: <span style={{ color: colors.black }}>$ {bcuRate.ui.toFixed(4)}</span></span>}
              {bcuRate.ur && <span style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>UR: <span style={{ color: colors.black }}>$ {bcuRate.ur.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>}
              {bcuRate.sell && <span style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>USD: <span style={{ color: colors.black }}>$ {bcuRate.sell.toFixed(2)}</span></span>}
            </div>
          </div>
        </div>
      )}

      <div style={styles.kpiRow}>
        <div style={styles.kpiCard}><div style={styles.kpiValue}>{stats.total}</div><div style={styles.kpiLabel}>Total</div></div>
        <div style={styles.kpiCard}><div style={styles.kpiValue}>{stats.activos}</div><div style={styles.kpiLabel}>Activos</div></div>
        <div style={styles.kpiCard}><div style={styles.kpiValue}>{stats.incluidos}</div><div style={styles.kpiLabel}>En cierre</div></div>
        <div style={styles.kpiCard}><div style={styles.kpiValue}>{formatCurrencyUY(stats.montoActivo)}</div><div style={styles.kpiLabel}>Monto activo (UYU)</div></div>
      </div>

      <div style={styles.filterRow}>
        <div style={styles.searchBox}>
          <FaSearch size={14} color={colors.gray400} />
          <input
            style={styles.searchInput}
            placeholder="Buscar por concepto, unidad o residente"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={styles.filterTabs}>
          {[
            { id: "todos", label: "Todos" },
            { id: "activa", label: "Activos" },
            { id: "incluida_en_cierre", label: "En cierre" },
            { id: "pagada", label: "Pagados" },
            { id: "anulada", label: "Anulados" },
          ].map((t) => (
            <button
              key={t.id}
              style={{ ...styles.filterTab, ...(filter === t.id ? styles.filterTabActive : {}) }}
              onClick={() => setFilter(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <LoadingState message="Cargando cargos..." />
      ) : filtered.length === 0 ? (
        <div style={styles.emptyState}>
          <FaReceipt size={40} color={colors.gray300} />
          <p style={{ color: colors.gray500, marginTop: 12 }}>No hay cargos para mostrar</p>
        </div>
      ) : (
        <div style={styles.list}>
          {filtered.map((c) => {
            const conv = ["UI", "UR", "USD"].includes((c.currency || "UYU").toUpperCase());
            return (
              <div key={c.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardLeft}>
                    <span style={styles.unitBadge}>Unidad {c.unitNumber || "-"}</span>
                    <StatusBadge variant={statusVariant[c.status] || "neutral"}>{statusLabel[c.status] || c.status}</StatusBadge>
                    {c.status !== "anulada" && (
                      <span style={styles.reserveBadge} title={`Fondo: ${fundName(c.targetFundId)}`}>
                        <FaMoneyBillWave size={10} /> {fundName(c.targetFundId)}
                      </span>
                    )}
                  </div>
                  <div style={styles.cardAmount}>
                    {conv ? `${Number(c.amount).toLocaleString("es-UY")} ${c.currency}` : formatCurrencyUY(Number(c.amount))}
                  </div>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.cardRow}>
                    <FaReceipt size={13} color={colors.gray400} />
                    <span style={styles.cardText}>{c.concept}</span>
                  </div>
                  <div style={styles.cardRow}>
                    <FaUser size={13} color={colors.gray400} />
                    <span style={styles.cardText}>
                      {usersMap[String(c.userid)] || `ID ${c.userid}`}
                      <span style={styles.cardSubtext}> · {c.userType === "tenant" ? "Inquilino" : "Propietario"}</span>
                    </span>
                  </div>
                  <div style={styles.cardRow}>
                    <span style={styles.cardSubtext}>Creado {formatDate(c.createdAt)}{c.createdBy ? ` por ${c.createdBy}` : ""}</span>
                  </div>
                  {c.status === "pagada" && (
                    <div style={styles.cardRow}>
                      <span style={styles.cardSubtext}>
                        {c.paidAutomatically
                          ? `Pagado automáticamente con los gastos comunes${c.paidAt ? ` el ${formatDate(c.paidAt)}` : ""}`
                          : `Marcado pagado${c.paidAt ? ` el ${formatDate(c.paidAt)}` : ""}`}
                      </span>
                    </div>
                  )}
                  {c.status === "anulada" && c.annulledReason && (
                    <div style={styles.cardRow}>
                      <span style={styles.cardSubtext}>Anulado: {c.annulledReason}</span>
                    </div>
                  )}
                </div>
                {(c.status === "activa") && (
                  <div style={styles.cardActions}>
                    <button style={styles.btnSecondary} onClick={() => setEditModal({ ...c })}>
                      <FaPen size={12} /> Editar
                    </button>
                    <button style={styles.btnSuccess} onClick={() => setPayModal(c)}>
                      <FaCheck size={12} /> Marcar pagado
                    </button>
                    <button style={styles.btnDanger} onClick={() => setAnnulModal(c)}>
                      <FaBan size={12} /> Anular
                    </button>
                  </div>
                )}
                {/* En cierre: se marca pagado SOLO cuando se cobra el gasto común que
                    lo incluye. Respaldo manual por si se cobró por fuera (efectivo). */}
                {c.status === "incluida_en_cierre" && (
                  <div style={styles.cardActions}>
                    <button style={styles.btnSuccess} onClick={() => setPayModal(c)}>
                      <FaCheck size={12} /> Marcar pagado
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Form crear */}
      <AdminModal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Nuevo cargo puntual"
        size="medium"
        footer={
          <>
            <button style={styles.btnSecondary} onClick={() => setShowForm(false)}>Cancelar</button>
            <button
              style={{ ...styles.btnPrimary, ...((saving || !form.userid) ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
              onClick={handleCreate}
              disabled={saving || !form.userid}
              title={!form.userid ? "La unidad no tiene un responsable asignado para ese rol" : undefined}
            >
              {saving ? "Guardando..." : "Crear cargo"}
            </button>
          </>
        }
      >
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Unidad *</label>
            <select style={styles.select} value={form.unitId} onChange={(e) => setForm({ ...form, unitId: e.target.value })}>
              <option value="">Seleccionar unidad</option>
              {apartments
                .slice()
                .sort((a, b) => String(a.number || "").localeCompare(String(b.number || ""), "es", { numeric: true }))
                .map((a) => (
                  <option key={a.id} value={a.id}>Unidad {a.number || a.id}</option>
                ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Responsable</label>
            <select style={styles.select} value={form.userType} onChange={(e) => setForm({ ...form, userType: e.target.value })}>
              <option value="owner">Propietario</option>
              <option value="tenant">Inquilino</option>
            </select>
          </div>

          {form.unitId && (
            <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
              <div style={styles.userDisplay}>
                <FaUser size={13} color={colors.gray500} />
                {form.userid
                  ? (usersMap[String(form.userid)] || `ID ${form.userid}`)
                  : "La unidad no tiene un responsable asignado para ese rol"}
              </div>
            </div>
          )}

          <div style={styles.formGroup}>
            <label style={styles.label}>Monto *</label>
            <input type="number" min="0" step="0.01" style={styles.input} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Moneda</label>
            <select style={styles.select} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              <option value="UYU">Pesos (UYU)</option>
              <option value="USD">Dólares (USD)</option>
              <option value="UI">Unidades Indexadas (UI)</option>
              <option value="UR">Unidades Reajustables (UR)</option>
            </select>
            {["UI", "UR", "USD"].includes(form.currency) && (
              <span style={{ fontSize: typography.sizes.sm, color: colors.gray500, marginTop: 4 }}>
                La conversión a pesos se hace al TC del día anterior al cierre.
              </span>
            )}
          </div>

          <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
            <label style={styles.label}>Concepto *</label>
            <input
              list="cargo-concept-suggestions"
              style={styles.input}
              placeholder="Ej: Control remoto de TV"
              value={form.concept}
              onChange={(e) => setForm({ ...form, concept: e.target.value })}
            />
            <datalist id="cargo-concept-suggestions">
              {CONCEPT_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>

          <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
            <label style={styles.label}>Fondo destino</label>
            <select style={styles.select} value={form.targetFundId} onChange={(e) => setForm({ ...form, targetFundId: e.target.value })}>
              {getAllActiveFunds(selectedBuilding).map((fund) => (
                <option key={fund.id} value={fund.id}>{fund.name}</option>
              ))}
            </select>
            <span style={{ fontSize: typography.sizes.sm, color: colors.gray500, marginTop: 4 }}>
              El monto cobrado ingresa a este fondo (por defecto, Caja).
            </span>
          </div>
        </div>
      </AdminModal>

      {/* Editar (solo activos) */}
      <AdminModal
        open={!!editModal}
        onClose={() => setEditModal(null)}
        title="Editar cargo"
        size="medium"
        footer={
          <>
            <button style={styles.btnSecondary} onClick={() => setEditModal(null)}>Cancelar</button>
            <button style={styles.btnPrimary} onClick={handleSaveEdit} disabled={saving}>
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </>
        }
      >
        {editModal && (
          <div style={styles.formGrid}>
            <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
              <label style={styles.label}>Concepto *</label>
              <input style={styles.input} value={editModal.concept || ""} onChange={(e) => setEditModal({ ...editModal, concept: e.target.value })} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Monto *</label>
              <input type="number" min="0" step="0.01" style={styles.input} value={editModal.amount} onChange={(e) => setEditModal({ ...editModal, amount: e.target.value })} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Moneda</label>
              <select style={styles.select} value={editModal.currency || "UYU"} onChange={(e) => setEditModal({ ...editModal, currency: e.target.value })}>
                <option value="UYU">Pesos (UYU)</option>
                <option value="USD">Dólares (USD)</option>
                <option value="UI">Unidades Indexadas (UI)</option>
                <option value="UR">Unidades Reajustables (UR)</option>
              </select>
            </div>
            <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
              <label style={styles.label}>Fondo destino</label>
              <select style={styles.select} value={editModal.targetFundId || "caja"} onChange={(e) => setEditModal({ ...editModal, targetFundId: e.target.value })}>
                {getAllActiveFunds(selectedBuilding).map((fund) => (
                  <option key={fund.id} value={fund.id}>{fund.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </AdminModal>

      {/* Pago */}
      <AdminModal
        open={!!payModal}
        onClose={() => setPayModal(null)}
        title="Confirmar pago"
        size="narrow"
        footer={
          <>
            <button style={styles.btnSecondary} onClick={() => setPayModal(null)}>Cancelar</button>
            <button style={styles.btnSuccess} onClick={() => handleMarkPaid(payModal)}>
              <FaCheck size={12} /> Confirmar pago
            </button>
          </>
        }
      >
        {payModal && (
          <div style={{ fontSize: typography.sizes.base, color: colors.gray900, lineHeight: 1.6 }}>
            <p>¿Confirmás que la unidad {payModal.unitNumber || "-"} pagó este cargo?</p>
            <p style={{ marginTop: 8 }}>
              <strong>{payModal.concept}</strong> — {formatCurrencyUY(Number(payModal.amount))}
            </p>
            <p style={{ marginTop: 8, color: colors.gray500, fontSize: typography.sizes.sm }}>
              {payModal?.status === "incluida_en_cierre"
                ? "Ya está dentro de un cierre: marcarlo pagado solo registra que se cobró por fuera (efectivo, etc.). Si se paga con los gastos comunes, se marca solo."
                : "Marcar como pagado lo saca del próximo cierre de mes."}
            </p>
          </div>
        )}
      </AdminModal>

      {/* Anular */}
      <AdminModal
        open={!!annulModal}
        onClose={() => { setAnnulModal(null); setAnnulReason(""); }}
        title="Anular cargo"
        size="narrow"
        footer={
          <>
            <button style={styles.btnSecondary} onClick={() => { setAnnulModal(null); setAnnulReason(""); }}>Cancelar</button>
            <button style={styles.btnDanger} onClick={handleAnnul}><FaBan size={12} /> Anular</button>
          </>
        }
      >
        <div style={styles.formGroup}>
          <label style={styles.label}>Motivo de anulación *</label>
          <textarea
            style={{ ...styles.input, minHeight: 80, resize: "vertical" }}
            placeholder="Ej: Cargado por error"
            value={annulReason}
            onChange={(e) => setAnnulReason(e.target.value)}
          />
        </div>
      </AdminModal>
    </div>
  );
};

const styles = {
  container: { padding: spacing[6], fontFamily: typography.fontFamily },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: spacing[5], flexWrap: "wrap", gap: spacing[3] },
  title: { ...pageTitle, fontSize: typography.sizes.xl, display: "flex", alignItems: "center" },
  subtitle: { fontSize: typography.sizes.base, color: colors.gray600, margin: "4px 0 0" },
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: spacing[3], marginBottom: spacing[5] },
  kpiCard: { background: colors.white, borderRadius: radius.md, padding: `${spacing[4]}px ${spacing[5]}px`, borderLeft: `4px solid ${colors.primary}`, boxShadow: shadows.sm },
  kpiValue: { fontSize: typography.sizes["2xl"], fontWeight: 400, color: colors.primary },
  kpiLabel: { fontSize: typography.sizes.sm, color: colors.gray600, marginTop: 2 },
  filterRow: { display: "flex", gap: spacing[3], marginBottom: spacing[4], flexWrap: "wrap", alignItems: "center" },
  searchBox: { display: "flex", alignItems: "center", gap: spacing[2], background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, padding: "6px 12px", flex: "1 1 220px", maxWidth: 320 },
  searchInput: { border: "none", outline: "none", fontSize: typography.sizes.base, flex: 1, background: "transparent", fontFamily: typography.fontFamily },
  filterTabs: { display: "flex", gap: spacing[1], flexWrap: "wrap" },
  filterTab: { padding: "6px 14px", borderRadius: radius.sm, border: `1px solid ${colors.gray200}`, background: colors.white, fontSize: typography.sizes.sm, fontWeight: 400, color: colors.gray900, cursor: "pointer", transition: `all ${transitions.fast}` },
  filterTabActive: { background: colors.primary, color: colors.white, borderColor: colors.primary },
  list: { display: "flex", flexDirection: "column", gap: spacing[3] },
  card: { ...cardBase, padding: spacing[4] },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  cardLeft: { display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" },
  unitBadge: { fontSize: typography.sizes.base, fontWeight: 400, color: colors.black, background: colors.gray100, borderRadius: 6, padding: "2px 10px" },
  reserveBadge: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 400, color: colors.successDark, background: colors.successLight, borderRadius: radius.md, padding: "2px 8px" },
  cardAmount: { fontSize: typography.sizes.xl, fontWeight: 400, color: colors.black },
  cardBody: { display: "flex", flexDirection: "column", gap: 6 },
  cardRow: { display: "flex", alignItems: "center", gap: spacing[2] },
  cardText: { fontSize: typography.sizes.base, color: colors.gray900 },
  cardSubtext: { fontSize: typography.sizes.sm, color: colors.gray400 },
  cardActions: { display: "flex", gap: spacing[2], marginTop: spacing[3], paddingTop: spacing[3], borderTop: `1px solid ${colors.gray100}` },
  emptyState: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px" },
  btnPrimary: { ...btnPrimary, padding: "8px 18px", fontSize: typography.sizes.base },
  btnSecondary: { ...btnSecondary, padding: "8px 18px", fontSize: typography.sizes.base },
  btnSuccess: { display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", background: colors.success, color: colors.white, border: "none", borderRadius: radius.sm, fontSize: typography.sizes.sm, fontWeight: 400, cursor: "pointer", transition: `all ${transitions.fast}` },
  btnDanger: { ...btnDanger, padding: "6px 14px", fontSize: typography.sizes.sm },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[4] },
  formGroup: { display: "flex", flexDirection: "column", gap: spacing[1] },
  label: { fontSize: typography.sizes.sm, fontWeight: 400, color: colors.gray900 },
  input: { ...inputBase, width: "auto", padding: "8px 12px", fontSize: typography.sizes.base },
  select: { padding: "8px 12px", border: `1px solid ${colors.gray300}`, borderRadius: radius.sm, fontSize: typography.sizes.base, outline: "none", background: colors.white, fontFamily: typography.fontFamily },
  userDisplay: { display: "flex", alignItems: "center", gap: spacing[2], padding: "8px 12px", background: colors.gray50, borderRadius: radius.sm, fontSize: typography.sizes.base, color: colors.gray900 },
};

export default CargosPuntualesAdmin;
